import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getPlaceDetails, getNearbyPlaces } from '../../services/maps';
import { loadGoogleMapsScript } from '../../services/googleMaps';
import PropTypes from 'prop-types';
import PlaceCard from './PlaceCard';
import { BeeLoader } from './loaders';
import { useFavorites } from '../../context/FavoritesContext';
import useGeolocation from '../../hooks/useGeolocation';

const Toast = ({ message, onDismiss }) => {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(onDismiss, 300);
    }, 2700);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 glass rounded-xl shadow-glass-lg ${
        exiting ? 'animate-toast-out' : 'animate-toast-in'
      }`}
    >
      <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center flex-shrink-0">
        <svg
          className="w-4 h-4 text-green-600 dark:text-green-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{message}</p>
    </div>
  );
};

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

const SkeletonCard = () => (
  <div className="glass-card p-4 animate-pulse">
    <div className="flex justify-between items-start gap-4">
      <div className="flex-1">
        <div className="h-5 bg-honey-100 dark:bg-honey-900/30 rounded-lg w-3/4 mb-2" />
        <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded-lg w-1/2" />
      </div>
      <div className="h-9 w-32 bg-honey-100 dark:bg-honey-900/30 rounded-full" />
    </div>
    <div className="mt-4">
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full w-full" />
    </div>
  </div>
);

const PlaceSearch = ({ onFavoriteAdded, onSearchStart, onSearchEnd }) => {
  const [inputValue, setInputValue] = useState('');
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const inputRef = useRef(null);
  const { addFavorite, isFavorite, refreshFavorites } = useFavorites();
  const { location, error: geoError, loading: geoLoading, requestLocation } = useGeolocation();

  const handlePlaceSelect = useCallback(
    async (place) => {
      if (!place?.place_id) {
        console.error('No place_id found');
        return;
      }

      setLoading(true);
      setError(null);
      onSearchStart?.();

      try {
        const results = await getPlaceDetails(place.place_id);

        if (results?.name) {
          setPlaces((prevPlaces) => {
            const newPlaces = [{ ...results }];
            return JSON.stringify(prevPlaces) === JSON.stringify(newPlaces)
              ? prevPlaces
              : newPlaces;
          });
        } else {
          setError('Invalid place data received');
          setPlaces([]);
        }
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to get place details');
        setPlaces([]);
      } finally {
        setLoading(false);
        onSearchEnd?.();
      }
    },
    [onSearchStart, onSearchEnd]
  );

  useEffect(() => {
    let isMounted = true;
    let autocomplete = null;

    const initializeAutocomplete = async () => {
      if (!inputRef.current) return;

      try {
        const maps = await loadGoogleMapsScript();
        if (!isMounted || !inputRef.current) return;

        autocomplete = new maps.places.Autocomplete(inputRef.current, {
          types: ['establishment'],
          fields: ['place_id', 'name', 'formatted_address'],
        });

        autocomplete.addListener('place_changed', () => {
          if (!isMounted || !autocomplete) return;

          const place = autocomplete.getPlace();
          setInputValue(inputRef.current?.value || '');
          handlePlaceSelect(place);
        });
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
        if (isMounted) {
          setError('Failed to initialize search');
        }
      }
    };

    initializeAutocomplete();

    return () => {
      isMounted = false;
      if (autocomplete && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [handlePlaceSelect]);

  const handleAddFavorite = (place) => {
    const favoritePlace = {
      name: place.name,
      address: place.address,
      current_popularity: place.current_popularity,
      business_status: place.business_status,
      place_id: place.place_id,
    };

    addFavorite(favoritePlace);
    refreshFavorites();
    setSuccessMessage(`${place.name} added to favorites!`);
    setPlaces(places.filter((p) => p.place_id !== place.place_id));
    setTimeout(() => setSuccessMessage(null), 3000);
    onFavoriteAdded?.();
  };

  const handleNearMe = useCallback(async () => {
    requestLocation();
  }, [requestLocation]);

  useEffect(() => {
    if (!location) return;
    let cancelled = false;

    const fetchNearby = async () => {
      setLoading(true);
      setError(null);
      onSearchStart?.();
      try {
        const results = await getNearbyPlaces(location.lat, location.lng);
        if (!cancelled) {
          setPlaces(results);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to get nearby places');
          setPlaces([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          onSearchEnd?.();
        }
      }
    };

    fetchNearby();
    return () => {
      cancelled = true;
    };
  }, [location, onSearchStart, onSearchEnd]);

  useEffect(() => {
    if (geoError) {
      setError(geoError);
    }
  }, [geoError]);

  const clearSearch = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setInputValue('');
    setPlaces([]);
    setError(null);
    onSearchEnd?.();
  };

  return (
    <>
      {successMessage && (
        <Toast message={successMessage} onDismiss={() => setSuccessMessage(null)} />
      )}

      <div className="space-y-6">
        <div className="space-y-2">
          <label
            htmlFor="place-search-input"
            className="block text-sm font-medium text-gray-600 dark:text-gray-300"
          >
            Search for a place
          </label>

          <div className="relative group">
            <input
              ref={inputRef}
              id="place-search-input"
              className="w-full p-4 pl-12 glass rounded-xl text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-honey-400/50 focus:border-honey-400
                transition-all duration-300 text-base"
              type="text"
              placeholder="Try 'Alexanderplatz' or your favorite restaurant..."
              onChange={(e) => setInputValue(e.target.value)}
              disabled={!!error}
            />
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-honey-600 transition-transform duration-300 group-focus-within:scale-110"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {inputValue && (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center
                  rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-600 dark:hover:text-gray-200
                  transition-all duration-200 focus:outline-none"
                aria-label="Clear search"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          <button
            onClick={handleNearMe}
            disabled={geoLoading}
            className="mt-2 flex items-center gap-2 px-4 py-2 min-h-[44px] glass rounded-xl text-sm font-medium text-honey-600 dark:text-honey-400
              hover:bg-honey-50 dark:hover:bg-honey-900/20 transition-all duration-300 active:scale-95"
            aria-label="Find places near me"
          >
            {geoLoading ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            )}
            Near Me
          </button>

          {!places.length && !loading && !error && (
            <p className="text-xs text-gray-400 mt-1 pl-1">
              Type and select a suggestion to see occupancy details
            </p>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm border border-red-100 dark:border-red-800/30 rounded-xl animate-scale-in">
            <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => {
                setError(null);
              }}
              className="ml-auto text-xs text-red-400 hover:text-red-600 font-medium transition-colors"
            >
              Dismiss
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center mt-8 space-y-4">
            <BeeLoader />
            <p className="text-sm text-gray-400 dark:text-gray-500 font-light">
              Searching for place details...
            </p>
            <div className="w-full max-w-md space-y-3 mt-4">
              <SkeletonCard />
            </div>
          </div>
        ) : (
          places.length > 0 && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Results</h2>
                <span className="text-xs text-gray-400 font-light">Click heart to save</span>
              </div>
              {places.map((place, index) => (
                <div
                  key={place.place_id || index}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <PlaceCard
                    place={place}
                    buttonText={isFavorite(place.place_id) ? 'Saved' : 'Save'}
                    onButtonClick={handleAddFavorite}
                    disabled={isFavorite(place.place_id)}
                  />
                </div>
              ))}
            </div>
          )
        )}

        {!places.length && !loading && !error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-honey-50 dark:bg-honey-900/20 flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-honey-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <p className="text-gray-400 text-sm font-light">
              Search for a place to check its occupancy
            </p>
          </div>
        )}
      </div>
    </>
  );
};

PlaceSearch.propTypes = {
  onFavoriteAdded: PropTypes.func,
  onSearchStart: PropTypes.func,
  onSearchEnd: PropTypes.func,
};

export default PlaceSearch;
